import ExcelJS from "exceljs"
import type {
  Border,
  BorderStyle,
  Borders,
  CellRichTextValue,
  Font,
  RichText,
  Style,
} from "exceljs"

import {
  bannerElementOwners,
  bannerLetterLabel,
  letterRuns,
  sigLegendLines,
  sigSourceOf,
  STATISTIC_LABEL,
  toExcelNumFmt,
} from "utils/tableModel"
import type {
  CellSig,
  HeaderNode,
  ModelCell,
  SigMeta,
  TableModel,
} from "utils/tableModel"
import {
  applySigStyle,
  resolveCellStyle,
  rowBands,
} from "shared/ui/Table/style/resolveCellStyle"
import type { CellContext } from "shared/ui/Table/style/resolveCellStyle"
import type {
  ResolvedStyle,
  TableStyle,
} from "shared/ui/Table/style/tableStyle.types"
import { downloadDataAsFile } from "utils/files/downloadUtils"

/**
 * Model-driven Excel export: one TableModel per table, styled through the
 * same `TableStyle` / `resolveCellStyle` contract as the on-screen DataTable.
 *
 * The sheet mirrors the DataTable grid (and so the engine layout): `freeze.y`
 * header rows and `freeze.x` stub columns, merged header cells, real numbers
 * with an Excel number format, widths from `style.excel` and frozen panes.
 *
 * A table run with `SIG:` gets the WinCross layout: a column-letters row
 * ("(A)", "(B)"...) under the banner (a column beside the row headers when
 * the banner runs down), the engine's `sig` rows under the tested rows with
 * ▲/▼ added from the vs-rest results, tint fills on significant cells,
 * greyed low-base columns and the legend lines under the table.
 */

/** One table to export: its sheet name (separate) or title row (combined). */
export interface TableExport {
  name: string
  model: TableModel
  /** Style for this table; `buildWorkbook` falls back to its `opts.style`. */
  style?: TableStyle
}

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
/** Font written for a family of "inherit" (or a generic CSS family). */
export const EXCEL_DEFAULT_FONT_NAME = "Calibri"
/** Sheet holding every table in "combined" mode. */
export const COMBINED_SHEET_NAME = "All Tables"
/** Blank rows between two tables in "combined" mode. */
const COMBINED_GAP_ROWS = 2
const MAX_SHEET_NAME = 31

// ---------------------------------------------------------------------------
// CSS → ExcelJS style mapping
// ---------------------------------------------------------------------------

const NAMED_COLORS: Readonly<Record<string, string>> = {
  black: "000000",
  white: "FFFFFF",
  red: "FF0000",
  green: "008000",
  blue: "0000FF",
  yellow: "FFFF00",
  gray: "808080",
  grey: "808080",
  silver: "C0C0C0",
}

const toHexByte = (value: number): string =>
  Math.max(0, Math.min(255, Math.round(value)))
    .toString(16)
    .padStart(2, "0")

/**
 * CSS colour → ExcelJS ARGB ("#f0f0f0" → "FFF0F0F0").
 * Supports #rgb, #rgba, #rrggbb, #rrggbbaa, rgb()/rgba() and a few names.
 * "inherit", "transparent" and anything unrecognised give undefined, which
 * leaves the workbook default in place.
 */
export const cssColorToArgb = (
  color: string | undefined
): string | undefined => {
  if (!color) return undefined
  const value = color.trim().toLowerCase()

  const hex = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/.exec(value)
  if (hex) {
    const digits =
      hex[1].length <= 4
        ? hex[1]
            .split("")
            .map((digit) => digit + digit)
            .join("")
        : hex[1]
    const alpha = digits.length === 8 ? digits.slice(6, 8) : "ff"
    return `${alpha}${digits.slice(0, 6)}`.toUpperCase()
  }

  const rgb =
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(\d*\.?\d+)(%?)\s*)?\)$/.exec(
      value
    )
  if (rgb) {
    const alphaRaw = rgb[4] === undefined ? 1 : Number(rgb[4])
    const alpha = rgb[5] === "%" ? alphaRaw / 100 : alphaRaw
    return [alpha * 255, Number(rgb[1]), Number(rgb[2]), Number(rgb[3])]
      .map(toHexByte)
      .join("")
      .toUpperCase()
  }

  const named = NAMED_COLORS[value]
  return named ? `FF${named}` : undefined
}

const CSS_BORDER_STYLES = new Set([
  "none",
  "hidden",
  "solid",
  "dashed",
  "dotted",
  "double",
  "groove",
  "ridge",
  "inset",
  "outset",
])

const CSS_BORDER_WIDTHS: Readonly<Record<string, number>> = {
  thin: 1,
  medium: 3,
  thick: 5,
}

/**
 * CSS border shorthand → ExcelJS border edge.
 * "1px solid black" → thin black, "2px solid #e2e8f0" → medium; dashed,
 * dotted and double keep their line style. "none" gives undefined.
 */
export const cssBorderToExcel = (
  border: string | undefined
): Partial<Border> | undefined => {
  if (!border) return undefined
  let width = 1
  let line = "solid"
  let color: string | undefined
  // Split on spaces, keeping "rgb(1, 2, 3)" in one token.
  for (const token of border.trim().split(/\s+(?![^(]*\))/)) {
    const lower = token.toLowerCase()
    const pixels = /^(\d*\.?\d+)px$/.exec(lower)
    if (pixels) width = Number(pixels[1])
    else if (lower in CSS_BORDER_WIDTHS) width = CSS_BORDER_WIDTHS[lower]
    else if (CSS_BORDER_STYLES.has(lower)) line = lower
    else if (lower !== "") color = token
  }
  if (line === "none" || line === "hidden" || width <= 0) return undefined

  const style: BorderStyle =
    line === "dashed"
      ? width >= 2
        ? "mediumDashed"
        : "dashed"
      : line === "dotted"
        ? "dotted"
        : line === "double"
          ? "double"
          : width <= 1
            ? "thin"
            : width <= 2
              ? "medium"
              : "thick"
  const argb = cssColorToArgb(color)
  return argb ? { style, color: { argb } } : { style }
}

const GENERIC_FONT_FAMILIES = new Set([
  "inherit",
  "initial",
  "unset",
  "serif",
  "sans-serif",
  "monospace",
  "cursive",
  "fantasy",
  "system-ui",
  "ui-serif",
  "ui-sans-serif",
  "ui-monospace",
])

/**
 * Excel font name of a CSS font stack: its first family, or
 * `EXCEL_DEFAULT_FONT_NAME` (Calibri) for "inherit", a generic family or none.
 */
const excelFontName = (family: string): string => {
  const first = family
    .split(",")[0]
    ?.trim()
    .replace(/^["']|["']$/g, "")
  if (!first || GENERIC_FONT_FAMILIES.has(first.toLowerCase())) {
    return EXCEL_DEFAULT_FONT_NAME
  }
  return first
}

/**
 * ExcelJS style of a resolved cell style. A font family of "inherit" is
 * written as Calibri; a font colour of "inherit" is left out (Excel's
 * automatic colour). `border` gives every edge; `borderTop` and
 * `borderBottom` replace the top and bottom edges when set ("none" removes
 * the edge).
 * @param numFmt Excel number format of a numeric value, if any
 */
export const toExcelCellStyle = (
  resolved: ResolvedStyle,
  numFmt?: string
): Partial<Style> => {
  const font: Partial<Font> = {
    name: excelFontName(resolved.fontFamily),
    size: resolved.fontSizePt,
    bold: resolved.bold,
    italic: resolved.italic,
  }
  const fontColor = cssColorToArgb(resolved.color)
  if (fontColor) font.color = { argb: fontColor }

  const style: Partial<Style> = {
    font,
    alignment: {
      horizontal: resolved.align,
      vertical: "middle",
      wrapText: resolved.wrap,
    },
  }

  const fill = cssColorToArgb(resolved.bg)
  if (fill) {
    style.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill } }
  }

  const edge = cssBorderToExcel(resolved.border)
  const top =
    resolved.borderTop !== undefined
      ? cssBorderToExcel(resolved.borderTop)
      : edge
  const bottom =
    resolved.borderBottom !== undefined
      ? cssBorderToExcel(resolved.borderBottom)
      : edge
  if (edge || top || bottom) {
    const borders: Partial<Borders> = {}
    if (top) borders.top = { ...top }
    if (edge) {
      borders.left = { ...edge }
      borders.right = { ...edge }
    }
    if (bottom) borders.bottom = { ...bottom }
    style.border = borders
  }

  if (numFmt) style.numFmt = numFmt
  return style
}

// ---------------------------------------------------------------------------
// Sheet writing
// ---------------------------------------------------------------------------

type ExcelValue = string | number | CellRichTextValue | null

/** A header node and its level in the tree (pre-order). */
interface PlacedNode {
  node: HeaderNode
  level: number
  isLeafLevel: boolean
}

const placeNodes = (tree: HeaderNode[]): PlacedNode[] => {
  const placed: PlacedNode[] = []
  const walk = (nodes: HeaderNode[], level: number): void => {
    nodes.forEach((node) => {
      if (node.span[1] <= node.span[0]) return
      placed.push({ node, level, isLeafLevel: node.children.length === 0 })
      walk(node.children, level + 1)
    })
  }
  walk(tree, 0)
  return placed
}

/** Header / label text; an empty label leaves the cell blank. */
const labelValue = (label: string | undefined): ExcelValue =>
  label === undefined || label === "" ? null : label

/** Finite numbers stay numbers; strings (letters, text) stay text; the rest is blank. */
const bodyValue = (cell: ModelCell | undefined): ExcelValue => {
  const raw = cell?.raw
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null
  if (typeof raw === "string" && raw !== "") return raw
  return null
}

/** Style of a title line (table name, or a title that does not fit the corner). */
const titleStyle = (style: TableStyle): Partial<Style> => {
  const font: Partial<Font> = {
    name: excelFontName(style.font.family),
    size: style.font.sizePt,
    bold: true,
  }
  const fontColor = cssColorToArgb(style.font.color)
  if (fontColor) font.color = { argb: fontColor }
  return { font, alignment: { horizontal: "left", vertical: "middle" } }
}

const writeTitle = (
  ws: ExcelJS.Worksheet,
  row: number,
  text: string,
  style: TableStyle
): void => {
  const cell = ws.getCell(row, 1)
  cell.value = text
  cell.style = titleStyle(style) as Style
}

/** Where the parts of a table go, relative to the row it starts on. */
interface TableGeometry {
  fx: number
  fy: number
  hasStatColumn: boolean
  hasStatRow: boolean
  acrossLevels: number
  downLevels: number
  titleCols: number
  titleRows: number
  hasCornerBlock: boolean
  /** Rows written before the header rows (a title line without a corner block). */
  leadRows: number
  /** Significance block driving the WinCross layout (tables run with `SIG:`). */
  sig?: SigMeta
  /** 1 when a column-letters row sits under the column headers (banner across). */
  lettersRows: number
  /** 1 when a column-letters column sits after the row headers (banner down). */
  lettersCols: number
}

const tableGeometry = (model: TableModel): TableGeometry => {
  const { x: fx, y: fy } = model.freeze
  const hasStatColumn = model.statAxis === "down" && fx >= 1
  const hasStatRow = model.statAxis === "across" && fy >= 1
  const titleCols = hasStatColumn ? fx - 1 : fx
  const titleRows = hasStatRow ? fy - 1 : fy
  const hasCornerBlock = titleCols > 0 && titleRows > 0
  const sig =
    (model.significance?.elements.length ?? 0) > 0
      ? model.significance
      : undefined
  return {
    fx,
    fy,
    hasStatColumn,
    hasStatRow,
    acrossLevels: Math.max(0, fy - (hasStatRow ? 1 : 0)),
    downLevels: Math.max(0, fx - (hasStatColumn ? 1 : 0)),
    titleCols,
    titleRows,
    hasCornerBlock,
    leadRows: model.title && !hasCornerBlock ? 1 : 0,
    ...(sig ? { sig } : {}),
    lettersRows: sig?.bannerAxis === "across" ? 1 : 0,
    lettersCols: sig?.bannerAxis === "down" ? 1 : 0,
  }
}

/**
 * The frozen-pane split of a table written at `startRow` (1-based): the stub
 * columns (`freeze.x`, plus the column-letters column when the banner runs
 * down) and every row above its first data row, i.e. the model's `freeze.y`
 * header rows plus the column-letters row, shifted by the rows written above
 * the table (the sheet rows before `startRow` plus the table's lead rows).
 */
export const tableFreezePane = (
  model: TableModel,
  startRow: number
): { xSplit: number; ySplit: number } => {
  const { fx, fy, leadRows, lettersRows, lettersCols } = tableGeometry(model)
  return {
    xSplit: fx + lettersCols,
    ySplit: startRow - 1 + leadRows + fy + lettersRows,
  }
}

// ---------------------------------------------------------------------------
// Significance (WinCross layout)
// ---------------------------------------------------------------------------

/** Font of a rich-text run: the cell's font with another colour / weight. */
const runFont = (
  base: Partial<Font> | undefined,
  color: string,
  bold: boolean
): Partial<Font> => {
  const argb = cssColorToArgb(color)
  return { ...base, bold, ...(argb ? { color: { argb } } : {}) }
}

/**
 * A `sig` cell's value: the tested cell's letters in their level styles,
 * then ▲/▼ from its vs-rest result ("Df ▲"). Plain text or null when there
 * is nothing to colour.
 */
const lettersValue = (
  cellSig: CellSig,
  font: Partial<Font> | undefined,
  style: TableStyle
): CellRichTextValue | null => {
  const runs: RichText[] = letterRuns(cellSig).map((run) => ({
    text: run.text,
    font: runFont(font, style.sig[run.level].text, style.sig[run.level].bold),
  }))
  const vsRest = cellSig.vsRest
  if (vsRest) {
    const arrow = vsRest.dir === "up" ? style.sig.arrowUp : style.sig.arrowDown
    runs.push({
      text: `${runs.length > 0 ? " " : ""}${arrow.glyph}`,
      font: runFont(font, arrow.color, false),
    })
  }
  return runs.length > 0 ? { richText: runs } : null
}

/**
 * Writes one table model at `startRow` (1-based) from column A and returns
 * the next free row.
 *
 * Layout (same as DataTable with the letters shown as rows):
 * - a title line first when the model has a title but no corner block for it;
 * - `freeze.y` header rows: the corner (title block + "Statistic"), the column
 *   header tree with merged spans, and the statistic row under /T;
 * - one row per down leaf: the row header tree (merged), the statistic
 *   column when stats run down, then the data cells as numbers with numFmt;
 * - column widths from `style.excel` (row header columns: `stubWidth`, the
 *   statistic and data columns: `dataWidth`), never narrowing a column;
 * - borders from `resolveCellStyle` with each merged cell's span and the
 *   model's row bands, so the style's `rules` and `bandRule` land on the
 *   edges the merged cells reach.
 *
 * With significance (the WinCross layout):
 * - a column-letters row, "(A)" "(B)"... merged over each banner element,
 *   under the column headers (before the statistic row under /T); when the
 *   banner runs down it is a column after the row headers instead. The
 *   corner block and the "Statistic" label stretch over it. The letters row
 *   takes the header rule (`rules.headerBottom`) from the banner, except
 *   under /T where the statistic row below it keeps the rule;
 * - the `sig` rows keep their place under the tested rows, their letters in
 *   the level styles and ▲/▼ appended from the vs-rest result;
 * - tested cells get the tint of their strongest "higher" result, and every
 *   cell of a low-base element is greyed (`applySigStyle`);
 * - the legend lines follow the table, one per row in column A: the
 *   engine's legend, the display-only lines for the low-base marker and the
 *   shading (`sigLegendLines`), then the notes.
 *
 * It never sets the sheet's views: freezing is the caller's decision (see
 * `buildWorkbook` and `tableFreezePane`).
 */
export const writeTableModel = (
  ws: ExcelJS.Worksheet,
  model: TableModel,
  style: TableStyle,
  startRow: number
): number => {
  const {
    fx,
    fy,
    hasStatColumn,
    hasStatRow,
    acrossLevels,
    downLevels,
    titleCols,
    titleRows,
    hasCornerBlock,
    leadRows,
    sig,
    lettersRows,
    lettersCols,
  } = tableGeometry(model)
  const bands = rowBands(model)

  if (leadRows > 0 && model.title) writeTitle(ws, startRow, model.title, style)
  const top = startRow + leadRows

  /** Resolves grid cell (y, x) with the row bands; `ctx` gives a merged cell's span. */
  const resolve = (
    y: number,
    x: number,
    ctx: CellContext = {}
  ): ResolvedStyle =>
    resolveCellStyle(model, y, x, style, "excel", { bands, ...ctx })

  // Grid (y, x) → sheet (row, column): the letters row sits after the column
  // header levels, the letters column after the row header levels.
  const sheetRow = (y: number): number =>
    top + y + (lettersRows > 0 && y >= acrossLevels ? 1 : 0)
  const sheetCol = (x: number): number =>
    1 + x + (lettersCols > 0 && x >= downLevels ? 1 : 0)

  /** Writes a styled cell at sheet (row, col), merged over rows × cols when larger than 1×1. */
  const write = (
    row: number,
    col: number,
    value: ExcelValue,
    cellStyle: Partial<Style>,
    rows: number = 1,
    cols: number = 1
  ): void => {
    const cell = ws.getCell(row, col)
    cell.value = value
    cell.style = cellStyle as Style
    // Merged cells take the master's style (so the borders run all round).
    if (rows > 1 || cols > 1) {
      try {
        ws.mergeCells(row, col, row + rows - 1, col + cols - 1)
      } catch {
        // An overlapping span (malformed header tree) stays unmerged rather
        // than failing the whole export.
      }
    }
  }

  /**
   * Writes grid cell (y, x) = data[y][x], merged over rows × cols grid cells;
   * `extraRows` / `extraCols` stretch the merge over the letters row/column.
   */
  const put = (
    y: number,
    x: number,
    value: ExcelValue,
    resolved: ResolvedStyle,
    rows: number = 1,
    cols: number = 1,
    numFmt?: string,
    extraRows: number = 0,
    extraCols: number = 0
  ): void => {
    const row = sheetRow(y)
    const col = sheetCol(x)
    write(
      row,
      col,
      value,
      toExcelCellStyle(resolved, numFmt),
      sheetRow(y + rows - 1) - row + 1 + extraRows,
      sheetCol(x + cols - 1) - col + 1 + extraCols
    )
  }

  // Corner: the title block and the "Statistic" label; both stretch over the
  // column-letters row or column.
  if (hasCornerBlock) {
    put(
      0,
      0,
      labelValue(model.title),
      resolve(0, 0, { rowSpan: titleRows, headerRule: titleRows === fy }),
      titleRows,
      titleCols,
      undefined,
      lettersRows,
      lettersCols
    )
  } else {
    // No title block, but the letters row/column still gets a corner cell.
    const rows = titleRows + lettersRows
    const cols = titleCols + lettersCols
    if (lettersRows + lettersCols > 0 && rows > 0 && cols > 0) {
      const corner = resolve(0, 0, {
        rowSpan: Math.max(1, titleRows),
        headerRule: titleRows === fy,
      })
      write(top, 1, null, toExcelCellStyle(corner), rows, cols)
    }
  }
  if (hasStatColumn && fy > 0) {
    put(
      0,
      fx - 1,
      STATISTIC_LABEL,
      resolve(0, fx - 1, { rowSpan: fy, headerRule: true }),
      fy,
      1,
      undefined,
      lettersRows
    )
  }
  if (hasStatRow && fx > 0) {
    put(
      fy - 1,
      0,
      STATISTIC_LABEL,
      resolve(fy - 1, fx - 1),
      1,
      fx,
      undefined,
      0,
      lettersCols
    )
  }

  // Column headers: leaf-level nodes run down to the last header level.
  if (acrossLevels > 0) {
    placeNodes(model.across.tree)
      .filter((placed) => placed.level < acrossLevels)
      .forEach(({ node, level, isLeafLevel }) => {
        const x = fx + node.span[0]
        const rows = isLeafLevel ? acrossLevels - level : 1
        put(
          level,
          x,
          labelValue(node.label),
          resolve(level, x, {
            rowSpan: rows,
            // The banner never ends the header block under a letters row.
            ...(lettersRows > 0 ? { headerRule: false } : {}),
          }),
          rows,
          node.span[1] - node.span[0]
        )
      })
  }

  // Statistic row (stats across, /T).
  if (hasStatRow) {
    model.across.leaves.forEach((leaf, c) => {
      put(fy - 1, fx + c, labelValue(leaf.stat), resolve(fy - 1, fx + c))
    })
  }

  // Row headers: leaf-level nodes run across to the last stub level.
  if (downLevels > 0) {
    placeNodes(model.down.tree)
      .filter((placed) => placed.level < downLevels)
      .forEach(({ node, level, isLeafLevel }) => {
        const y = fy + node.span[0]
        const rows = node.span[1] - node.span[0]
        put(
          y,
          level,
          labelValue(node.label),
          resolve(y, level, { rowSpan: rows }),
          rows,
          isLeafLevel ? downLevels - level : 1
        )
      })
  }

  // Column letters: one merged "(A)" per banner element along the banner.
  if (sig) {
    const owners = bannerElementOwners(model)
    const across = lettersRows > 0
    let leaf = 0
    while (leaf < owners.length) {
      const index = owners[leaf]
      let end = leaf + 1
      if (index >= 0) {
        while (end < owners.length && owners[end] === index) end++
      }
      const element = index >= 0 ? sig.elements[index] : undefined
      // The letters line takes the look of the header cell next to it. A
      // letters row takes the header rule when it is the last header row
      // (under /T the statistic row follows it and keeps the rule); a
      // letters column takes the band edges of the rows it spans.
      const base = across
        ? resolve(Math.max(0, acrossLevels - 1), fx + leaf, {
            headerRule: !hasStatRow,
          })
        : resolve(fy + leaf, Math.max(0, downLevels - 1), {
            rowSpan: end - leaf,
          })
      const resolved: ResolvedStyle = {
        ...base,
        bold: false,
        // The letters row is never the table's top row.
        ...(across ? { borderTop: undefined } : {}),
        ...(element?.lowBase
          ? {
              color: style.lowBase.textColor,
              italic: base.italic || style.lowBase.italic,
            }
          : {}),
      }
      const label = element
        ? bannerLetterLabel(element, style.lowBase.marker)
        : ""
      const cellStyle = toExcelCellStyle(resolved)
      if (across) {
        write(
          top + acrossLevels,
          sheetCol(fx + leaf),
          label || null,
          cellStyle,
          1,
          end - leaf
        )
      } else {
        write(
          sheetRow(fy + leaf),
          1 + downLevels,
          label || null,
          cellStyle,
          end - leaf,
          1
        )
      }
      leaf = end
    }
  }

  // Statistic column and data cells.
  model.down.leaves.forEach((leaf, r) => {
    const y = fy + r
    if (hasStatColumn) {
      // A leaf with its own label (a dashboard measure's unit) shows it.
      put(y, fx - 1, labelValue(leaf.statLabel ?? leaf.stat), resolve(y, fx - 1))
    }
    model.across.leaves.forEach((_, c) => {
      const x = fx + c
      const cell = model.cells[r]?.[c]
      const resolved = sig
        ? applySigStyle(resolve(y, x), model, y, x, style)
        : resolve(y, x)
      let value: ExcelValue = bodyValue(cell)
      if (sig && cell?.role === "letters") {
        const source = sigSourceOf(model, r, c)
        const sourceSig = source
          ? model.cells[source.row][source.col].sig
          : undefined
        if (sourceSig) {
          value = lettersValue(
            sourceSig,
            toExcelCellStyle(resolved).font,
            style
          )
        }
      }
      const numFmt =
        typeof value === "number"
          ? toExcelNumFmt(resolved.numberFormat)
          : undefined
      put(y, x, value, resolved, 1, 1, numFmt)
    })
  })

  // Widths never shrink a column another table on the sheet widened.
  const totalCols = fx + lettersCols + model.across.leaves.length
  for (let x = 0; x < totalCols; x++) {
    const width = x < downLevels ? style.excel.stubWidth : style.excel.dataWidth
    const column = ws.getColumn(x + 1)
    if (!column.width || column.width < width) column.width = width
  }

  let next = top + fy + lettersRows + model.down.leaves.length

  // Legend lines under the table.
  if (sig) {
    const font: Partial<Font> = {
      name: excelFontName(style.font.family),
      size: style.font.sizePt,
      italic: style.excel.legendItalic,
    }
    const fontColor = cssColorToArgb(style.font.color)
    if (fontColor) font.color = { argb: fontColor }
    sigLegendLines(model, style).forEach((line) => {
      write(next, 1, line, {
        font,
        alignment: { horizontal: "left", vertical: "middle", wrapText: false },
      })
      next++
    })
  }

  return next
}

// ---------------------------------------------------------------------------
// Workbook
// ---------------------------------------------------------------------------

/**
 * A valid, unique Excel sheet name: no * ? : \ / [ ], no leading or trailing
 * apostrophe, at most 31 characters, not "History", unique ignoring case
 * ("Name (2)" on a clash). An empty name becomes "Sheet<index + 1>".
 */
export const toSheetName = (
  name: string,
  index: number,
  used: Set<string>
): string => {
  const clean = (value: string): string =>
    value
      .replace(/[*?:/\\[\]]/g, "")
      .trim()
      .replace(/^'+|'+$/g, "")
      .trim()
  const base =
    clean(clean(name).slice(0, MAX_SHEET_NAME)) || `Sheet${index + 1}`
  let candidate = base
  for (let n = 2; used.has(candidate.toLowerCase()); n++) {
    const suffix = ` (${n})`
    candidate = `${clean(base.slice(0, MAX_SHEET_NAME - suffix.length))}${suffix}`
  }
  used.add(candidate.toLowerCase())
  return candidate
}

/**
 * Writes table models into `workbook`, after the sheets it already holds.
 * - "separate": one sheet per table, named after it; each table starts on
 *   row 1.
 * - "combined": one "All Tables" sheet; each table gets a bold title row with
 *   its name, and two blank rows separate the tables.
 *
 * A sheet name the workbook already holds is taken: a table named like one
 * gets "Name (2)". A workbook left with no sheet at all gets a "Sheet1"
 * saying there is nothing to export, so it always opens.
 *
 * Each table is written in `table.style` when given, else `opts.style`;
 * combined mode's title rows use `opts.style`. Column widths never shrink,
 * so a combined sheet's columns are as wide as the widest table asked for.
 *
 * Freeze rule: a separate sheet holds one table and freezes when its table's
 * style says so (`excel.freezePanes`), at that table's data origin,
 * `tableFreezePane(model, startRow)`, which counts every row written above
 * the data. A combined sheet stacks tables of different shapes and is never
 * frozen.
 */
export const addTablesToWorkbook = (
  workbook: ExcelJS.Workbook,
  tables: TableExport[],
  opts: { mode: "separate" | "combined"; style: TableStyle }
): void => {
  const { mode, style } = opts
  // "History" is reserved by Excel.
  const used = new Set<string>([
    "history",
    ...workbook.worksheets.map((ws) => ws.name.toLowerCase()),
  ])

  if (mode === "combined") {
    const ws = workbook.addWorksheet(
      toSheetName(COMBINED_SHEET_NAME, 0, used)
    )
    let row = 1
    tables.forEach((table, index) => {
      if (index > 0) row += COMBINED_GAP_ROWS
      writeTitle(ws, row, table.name, style)
      row = writeTableModel(ws, table.model, table.style ?? style, row + 1)
    })
  } else {
    const startRow = 1
    tables.forEach((table, index) => {
      const ws = workbook.addWorksheet(toSheetName(table.name, index, used))
      const tableStyle = table.style ?? style
      writeTableModel(ws, table.model, tableStyle, startRow)
      if (tableStyle.excel.freezePanes) {
        const pane = tableFreezePane(table.model, startRow)
        if (pane.xSplit > 0 || pane.ySplit > 0) {
          ws.views = [{ state: "frozen", ...pane }]
        }
      }
    })
  }

  if (workbook.worksheets.length === 0) {
    workbook.addWorksheet("Sheet1").getCell(1, 1).value = "No tables to export"
  }
}

/** Builds a new workbook from table models (see `addTablesToWorkbook`). */
export const buildWorkbook = (
  tables: TableExport[],
  opts: { mode: "separate" | "combined"; style: TableStyle }
): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook()
  addTablesToWorkbook(workbook, tables, opts)
  return workbook
}

/** Writes the workbook and starts the browser download (".xlsx" is appended when missing). */
export const downloadWorkbook = async (
  wb: ExcelJS.Workbook,
  filename: string
): Promise<void> => {
  const buffer = await wb.xlsx.writeBuffer()
  const name = filename.toLowerCase().endsWith(".xlsx")
    ? filename
    : `${filename}.xlsx`
  downloadDataAsFile(buffer as ArrayBuffer, name, XLSX_MIME)
}
